import { Box, Link, Typography } from "@mui/material";
import { BaseTable } from "components";
import * as sort from "enums/sort";
import { UseMaterialColors, UseTheme } from "lib/hooks";
import { GradientHeaderText } from "lib/styles/header";
import { GetTableCellStyles, TableHeaderStyles } from "lib/styles/table";
import { SectorAdminContact, SectorAdminContactInfo } from "models/dto/user";
import { useMemo } from "react";

import { CreateHeadCells } from "./accessContactInfoHeadCells";

export type SectorAdminTableProps = {
	sectorData: SectorAdminContactInfo;
	loading: boolean;
	materialColors: ReturnType<typeof UseMaterialColors>;
	theme: ReturnType<typeof UseTheme>["theme"];
	/** Per-sector sort applied by the parent (tracks sort state by sector id). */
	sortAdmins: (
		admins: SectorAdminContact[],
		sectorId: string
	) => SectorAdminContact[];
	handleRequestSort: (
		sectorId: string,
		property: keyof SectorAdminContact
	) => void;
	getSortEnum: (sectorId: string) => ReturnType<typeof sort.Asc>;
};

/** Renders sector administrators for one sector (name and mailto email). */
export function SectorAdminTable({
	sectorData,
	loading,
	materialColors,
	theme,
	sortAdmins,
	handleRequestSort,
	getSortEnum,
}: SectorAdminTableProps) {
	const headCells = useMemo(() => {
		const fieldMap: Record<string, keyof SectorAdminContact> = {
			name: "name",
			email: "email",
		};
		return CreateHeadCells(materialColors, (property) => {
			const field = fieldMap[property];
			if (field) {
				handleRequestSort(sectorData.sectorId, field);
			}
		});
	}, [materialColors, sectorData.sectorId, handleRequestSort]);

	return (
		<BaseTable<SectorAdminContact, keyof SectorAdminContact>
			data={sortAdmins(sectorData.admins, sectorData.sectorId)}
			headCells={headCells}
			order={getSortEnum(sectorData.sectorId)}
			config={{
				stickyHeader: true,
				header: {
					title: (
						<Typography
							variant="h5"
							sx={GradientHeaderText(theme, false)}
						>
							{sectorData.sectorName}
						</Typography>
					),
				},
			}}
			loading={loading}
			hideActions={true}
			sx={TableHeaderStyles(theme)}
			rowMapper={(admin, cell) => {
				const cellStyles = GetTableCellStyles();

				if (cell.id === "name") {
					return (
						<Box component="span" sx={cellStyles.name}>
							{admin.name}
						</Box>
					);
				}
				if (cell.id === "email") {
					return (
						<Link
							href={`mailto:${admin.email}`}
							underline="hover"
							sx={cellStyles.email}
						>
							{admin.email}
						</Link>
					);
				}
				return undefined;
			}}
			getRowId={(admin) => admin.userId}
		/>
	);
}
